import {
  endOfDay,
  format,
  nextFriday,
  startOfDay,
  startOfWeek,
  subWeeks,
} from "date-fns";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useEffect } from "react";
import { useState } from "react";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Line,
  LineChart,
  LabelList,
} from "recharts";
import { formatNumber } from "src/components/numberingformat";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "src/components/shadcn/chart";
import { instance2 } from "src/components/utils/axiosInstance";
import { buttonVariants } from "src/components/shadcn/button";
import { Button } from "src/components/shadcn/button";

const chartConfig = {
  realisasi: {
    label: "Realisasi",
    color: "var(--primary)",
  },
  rencana: {
    label: "Rencana",
    color: "oklch(80.43% 0.215 18.82)",
  },
};

const RenderCustomLabel = (props) => {
  const { x, y, value, type } = props;

  const backgroundWidth = 40;
  const backgroundHeight = 20;
  const borderRadius = 3;

  const isRealisasi = !type || type === "realisasi";
  const color = isRealisasi ? "var(--color-realisasi)" : "var(--color-rencana)";
  const yOffset = isRealisasi ? 12 : -backgroundHeight - 10;

  if (value === null || value === undefined || value === 0) {
    return null;
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <rect
        x={-backgroundWidth / 2}
        y={yOffset}
        width={backgroundWidth}
        height={backgroundHeight}
        rx={borderRadius}
        ry={borderRadius}
        fill={color}
        strokeWidth={1}
        fillOpacity={0.7}
      />
      <text
        x={0}
        y={yOffset + backgroundHeight / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#fff"
        fontSize="12"
      >
        {formatNumber(value)}
      </text>
    </g>
  );
};

export const LaporanProduksiChart = () => {
  const [weekSubtract, setWeekSubtract] = useState(0);

  const refDate = subWeeks(new Date(), weekSubtract);
  const startOfRefWeek = startOfWeek(refDate, { weekStartsOn: 1 });
  const endOfRefWeek = nextFriday(startOfRefWeek);

  const goToNextWeek = () => setWeekSubtract((prev) => prev - 1);
  const goToPrevWeek = () => setWeekSubtract((prev) => prev + 1);

  return (
    <div className="grid grid-rows-[auto_1fr] gap-6 p-8 w-full h-full">
      <div className="flex flex-col gap-4">
        <h2 className=" text-xl sm:text-2xl font-medium text-balance text-center">
          Laporan Produksi
        </h2>
        <div className="grid grid-cols-[auto_1fr_1fr_auto] items-center gap-1">
          <Button onClick={goToPrevWeek} className="h-8" size="icon">
            <ChevronLeftIcon />
          </Button>
          <div
            className={buttonVariants({
              variant: "outline",
              className: "flex items-center gap-2 text-xs",
            })}
          >
            <p>{format(startOfRefWeek, "PP")}</p>
          </div>
          <div
            className={buttonVariants({
              variant: "outline",
              className: "flex items-center gap-2 text-xs",
            })}
          >
            <p>{format(endOfRefWeek, "PP")}</p>
          </div>
          <Button
            disabled={weekSubtract === 0}
            onClick={goToNextWeek}
            className="h-8"
            size="icon"
          >
            <ChevronRightIcon />
          </Button>
        </div>
      </div>
      <div className="flex justify-center items-center text-center">
        <Chart weekSubtract={weekSubtract} />
      </div>
    </div>
  );
};

const Chart = ({ weekSubtract }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const kitchenId = localStorage.getItem("kitchenId");
        const locationId = localStorage.getItem("locationId");

        if (!kitchenId && !locationId) {
          setLoading(false);
          return;
        }

        let url;
        if (kitchenId) {
          url = `/kitchen_result/${kitchenId}`;
        } else {
          url = `/kitchen_result/${locationId}`;
        }

        const refDate = subWeeks(new Date(), weekSubtract);
        const startOfRefWeek = startOfDay(
          startOfWeek(refDate, { weekStartsOn: 1 }),
        );
        const endOfRefWeek = endOfDay(nextFriday(startOfRefWeek));

        const response = await instance2.get(url);
        const apiData = response.data
          .filter((item) => {
            const itemDate = startOfDay(new Date(item.date));
            return itemDate >= startOfRefWeek && itemDate <= endOfRefWeek;
          })
          .map((item) => ({
            date: format(item.date, "dd MMM"),
            rencana: item.qty_prop,
            realisasi: item.qty_real,
          }));

        const allDates = [];
        for (let i = 0; i < 5; i++) {
          const date = new Date(startOfRefWeek);
          date.setDate(startOfRefWeek.getDate() + i);
          allDates.push(format(date, "dd MMM"));
        }

        const chartData = allDates.map((date) => {
          const foundData = apiData.find((item) => item.date === date);
          if (foundData) {
            return foundData;
          }
          return {
            date,
            rencana: 0,
            realisasi: 0,
          };
        });

        setChartData(chartData);
      } catch (err) {
        setError(err.message || "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [weekSubtract]);

  if (loading) return "Loading...";
  if (error) return <div>Error: {error}</div>;

  return (
    <ChartContainer config={chartConfig} className="min-h-96 h-full w-full">
      <LineChart
        margin={{ right: 20, top: 20 }}
        accessibilityLayer
        data={chartData}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="date"
          tickLine={false}
          tickMargin={10}
          axisLine={false}
        />
        <YAxis
          axisLine={false}
          label={{ value: "Jumlah Pax", angle: -90, position: "insideLeft" }}
          tickFormatter={(value) => formatNumber(value)}
        />
        <ChartTooltip content={<ChartTooltipContent />} />
        <ChartLegend content={<ChartLegendContent />} />
        <Line
          dataKey="rencana"
          stroke="var(--color-rencana)"
          fill="var(--color-rencana)"
          strokeWidth={2}
        >
          <LabelList
            dataKey="rencana"
            content={<RenderCustomLabel type="rencana" />}
          />
        </Line>
        <Line
          dataKey="realisasi"
          stroke="var(--color-realisasi)"
          fill="var(--color-realisasi)"
          strokeWidth={2}
        >
          <LabelList
            dataKey="realisasi"
            content={<RenderCustomLabel type="realisasi" />}
          />
        </Line>
      </LineChart>
    </ChartContainer>
  );
};
